/**
 * Interactive specs — typed, validated learning activities.
 *
 * The agent emits a spec, hand-built renderers execute it, and the result comes
 * back as skill evidence. This barrel holds the discriminated union over all 13
 * kinds, the validation boundary nothing reaches Kid Mode without crossing, the
 * repair pass for near-miss model output, and the mapping that makes playing an
 * activity an observation in its own right.
 */

import { z } from 'zod'
import type {
  EvidenceSignal,
  InteractiveKind,
  InteractiveResult,
  InteractiveSpec,
  Level,
  SkillEvidence,
} from '../types'
import { INTERACTIVE_KINDS } from '../types'

import { KIND_LABELS, SPEC_TOP_LEVEL_KEYS } from './common'
import { clampLevel } from '../rubric'
import { countWords } from './content'

import { TangramSpecSchema } from './kinds/tangram'
import { MatrixReasoningSpecSchema } from './kinds/matrix-reasoning'
import { SightWordDeckSpecSchema } from './kinds/sight-word-deck'
import { CountingGameSpecSchema } from './kinds/counting-game'
import { PatternBuilderSpecSchema } from './kinds/pattern-builder'
import { DeductionPuzzleSpecSchema } from './kinds/deduction-puzzle'
import { SortingGameSpecSchema } from './kinds/sorting-game'
import { SequenceOrderSpecSchema } from './kinds/sequence-order'
import { LetterTracingSpecSchema } from './kinds/letter-tracing'
import { ReadingTimerSpecSchema } from './kinds/reading-timer'
import { MemoryPairsSpecSchema } from './kinds/memory-pairs'
import { Model3dSpecSchema } from './kinds/model-3d'
import { StoryBuilderSpecSchema } from './kinds/story-builder'

export * from './common'
export * from './themes'
export * from './content'
export * from './strokes'
export * from './deduction'
export * from './difficulty'
export * from './defaults'

export * from './kinds/tangram'
export * from './kinds/matrix-reasoning'
export * from './kinds/sight-word-deck'
export * from './kinds/counting-game'
export * from './kinds/pattern-builder'
export * from './kinds/deduction-puzzle'
export * from './kinds/sorting-game'
export * from './kinds/sequence-order'
export * from './kinds/letter-tracing'
export * from './kinds/reading-timer'
export * from './kinds/memory-pairs'
export * from './kinds/model-3d'
export * from './kinds/story-builder'

// ─────────────────────────────────────────────────────────────
// The union
// ─────────────────────────────────────────────────────────────

export const SPEC_SCHEMAS = {
  tangram: TangramSpecSchema,
  matrix_reasoning: MatrixReasoningSpecSchema,
  sight_word_deck: SightWordDeckSpecSchema,
  counting_game: CountingGameSpecSchema,
  pattern_builder: PatternBuilderSpecSchema,
  deduction_puzzle: DeductionPuzzleSpecSchema,
  sorting_game: SortingGameSpecSchema,
  sequence_order: SequenceOrderSpecSchema,
  letter_tracing: LetterTracingSpecSchema,
  reading_timer: ReadingTimerSpecSchema,
  memory_pairs: MemoryPairsSpecSchema,
  model_3d: Model3dSpecSchema,
  story_builder: StoryBuilderSpecSchema,
} as const satisfies Record<InteractiveKind, z.ZodTypeAny>

export const InteractiveSpecSchema = z.discriminatedUnion('kind', [
  TangramSpecSchema,
  MatrixReasoningSpecSchema,
  SightWordDeckSpecSchema,
  CountingGameSpecSchema,
  PatternBuilderSpecSchema,
  DeductionPuzzleSpecSchema,
  SortingGameSpecSchema,
  SequenceOrderSpecSchema,
  LetterTracingSpecSchema,
  ReadingTimerSpecSchema,
  MemoryPairsSpecSchema,
  Model3dSpecSchema,
  StoryBuilderSpecSchema,
])

/** The spec union with each kind's config fully typed. */
export type TypedInteractiveSpec = z.infer<typeof InteractiveSpecSchema>

// ─────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────

export type ValidationResult =
  | { ok: true; spec: InteractiveSpec }
  | { ok: false; errors: string[] }

/**
 * Validate model output before it becomes a playable artifact.
 *
 * Errors are read by a person and by the model on its retry, so they name the
 * path and say what is wrong in words, never a code.
 */
export function validateSpec(input: unknown): ValidationResult {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['a spec must be an object'] }
  }

  const kind = (input as { kind?: unknown }).kind
  if (typeof kind !== 'string' || !(INTERACTIVE_KINDS as readonly string[]).includes(kind)) {
    return {
      ok: false,
      errors: [`kind: "${String(kind)}" is not one of ${INTERACTIVE_KINDS.join(', ')}`],
    }
  }

  // Dispatch on kind rather than parsing the union, so a bad tangram reports
  // tangram problems instead of thirteen sets of mismatch noise.
  const parsed = SPEC_SCHEMAS[kind as InteractiveKind].safeParse(input)
  if (!parsed.success) {
    return { ok: false, errors: parsed.error.issues.map(formatIssue) }
  }

  const spec = parsed.data as unknown as InteractiveSpec
  // An activity that logs an observation has to say what the observation is
  // evidence for, or the result lands nowhere.
  if (spec.successCriteria.autoLogObservation && spec.targetSkillIds.length === 0) {
    return {
      ok: false,
      errors: ['targetSkillIds: an activity that logs an observation must name the skills it is evidence for'],
    }
  }
  return { ok: true, spec }
}

export function isValidSpec(input: unknown): input is InteractiveSpec {
  return validateSpec(input).ok
}

function formatIssue(issue: z.ZodIssue): string {
  const path = issue.path.length > 0 ? issue.path.join('.') : '(root)'
  return `${path}: ${issue.message}`
}

// ─────────────────────────────────────────────────────────────
// Repair
// ─────────────────────────────────────────────────────────────

/** Config keys whose value is a number, for coercing "3" back to 3. */
const NUMERIC_KEYS = new Set([
  'snapTolerance', 'rotationSnapDegrees', 'rotationDeg', 'dx', 'dy', 'x', 'y',
  'correctIndex', 'count', 'gridColumns', 'targetNumber', 'distractorCount',
  'flashDurationMs', 'instantThresholdMs', 'passes', 'bestMs',
  'sequenceLength', 'revealCount', 'choiceCount',
  'pageCount', 'pairCount', 'columns', 'rows', 'faceDownRevealMs',
  'targetSeconds', 'attemptsThisSession', 'wordCount',
  'toleranceRadius', 'timeScale', 'difficulty',
  'shuffleSeed', 'scatterSeed', 'seed',
])

/** Config keys whose value is a boolean, for coercing "true" back to true. */
const BOOLEAN_KEYS = new Set([
  'rotationAllowed', 'flipAllowed', 'flipped', 'blank',
  'requireInstantRecognition', 'echoPrompt',
  'enforceOneToOne', 'allowRecount', 'chantAloud',
  'negate', 'allowElimination', 'celebrateAttempts', 'rereadHint',
  'revealItemsOneAtATime', 'gentleCorrection', 'showNumbers',
  'enforceStrokeOrder', 'enforceDirection', 'acceptInventedSpelling',
  'celebrateImprovementOnly', 'showLiveTimer', 'nameCardsAloud',
  'autoRotate', 'visibleByDefault', 'drawingEnabled', 'exportToBook',
  'neverCorrectSpelling', 'enabled', 'autoLogObservation',
])

function coerceScalars(value: unknown, key?: string): unknown {
  if (Array.isArray(value)) return value.map((entry) => coerceScalars(entry, key))
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, coerceScalars(v, k)]),
    )
  }
  if (typeof value !== 'string' || key === undefined) return value
  if (NUMERIC_KEYS.has(key)) {
    const asNumber = Number(value)
    return value.trim() !== '' && Number.isFinite(asNumber) ? asNumber : value
  }
  if (BOOLEAN_KEYS.has(key)) {
    if (value === 'true') return true
    if (value === 'false') return false
  }
  return value
}

/**
 * Best-effort coercion for near-miss model output.
 *
 * It fills structural defaults, drops keys that do not belong, and rebuilds
 * values that follow arithmetically from what is already there — a word count
 * from the passage, a grid layout from the number of pairs.
 *
 * It never invents semantic content. A puzzle with no clues, a grid with no
 * answer and a story with no problem all stay invalid, because a plausible
 * activity that teaches nothing is worse than an error message.
 */
export function repairSpec(input: unknown): unknown {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) return input

  const source = coerceScalars(input) as Record<string, unknown>
  const draft: Record<string, unknown> = {}
  // The schemas are strict, so anything the model invented at the top level goes.
  for (const key of SPEC_TOP_LEVEL_KEYS) {
    if (key in source) draft[key] = source[key]
  }

  const kind = typeof draft.kind === 'string' ? draft.kind : undefined

  if (typeof draft.title !== 'string' || draft.title.trim().length === 0) {
    draft.title = kind && kind in KIND_LABELS ? KIND_LABELS[kind as InteractiveKind] : 'Activity'
  }
  draft.difficulty = typeof draft.difficulty === 'number' && Number.isFinite(draft.difficulty)
    ? clampLevel(draft.difficulty)
    : 2

  if (!Array.isArray(draft.targetSkillIds)) draft.targetSkillIds = []

  const criteria = draft.successCriteria
  draft.successCriteria =
    criteria !== null && typeof criteria === 'object' && !Array.isArray(criteria)
      ? { autoLogObservation: true, ...(criteria as Record<string, unknown>) }
      : { autoLogObservation: true }

  if (typeof draft.theme !== 'string' || draft.theme.trim().length === 0) delete draft.theme

  if (draft.config === null || typeof draft.config !== 'object' || Array.isArray(draft.config)) {
    draft.config = {}
  }
  if (kind) repairConfig(kind, draft.config as Record<string, unknown>)

  return draft
}

/** Per-kind rebuilds. Each one is arithmetic or identity — never an answer. */
function repairConfig(kind: string, config: Record<string, unknown>): void {
  switch (kind) {
    case 'tangram': {
      if (config.pieceSet === undefined && Array.isArray(config.pieces)) {
        config.pieceSet = config.pieces.length === 7 ? 'classic7' : 'custom'
      }
      if (!Array.isArray(config.prePlacedPieceIds)) config.prePlacedPieceIds = []
      break
    }
    case 'sorting_game': {
      // The criteria are whatever the bins already sort on.
      if (!Array.isArray(config.criteria) && Array.isArray(config.bins)) {
        const first = config.bins[0] as { match?: Record<string, unknown> } | undefined
        if (first?.match && typeof first.match === 'object') {
          config.criteria = Object.keys(first.match)
        }
      }
      break
    }
    case 'letter_tracing': {
      if (Array.isArray(config.letters)) {
        for (const letter of config.letters as Array<Record<string, unknown>>) {
          if (letter?.letterCase === undefined && typeof letter?.char === 'string') {
            letter.letterCase = letter.char === letter.char.toUpperCase() ? 'upper' : 'lower'
          }
        }
      }
      break
    }
    case 'reading_timer': {
      const content = config.content as Record<string, unknown> | undefined
      if (content?.mode === 'passage' && typeof content.text === 'string') {
        content.wordCount = countWords(content.text)
      }
      break
    }
    case 'memory_pairs': {
      if (Array.isArray(config.pairs)) {
        const pairCount = config.pairs.length
        if (typeof config.pairCount !== 'number') config.pairCount = pairCount
        const layout = config.layout
        if (layout === null || typeof layout !== 'object') {
          config.layout = squarestLayout(pairCount * 2)
        }
      }
      break
    }
    default:
      break
  }
}

/** The most square columns×rows that holds exactly `cards`. */
function squarestLayout(cards: number): { columns: number; rows: number } {
  let best = { columns: cards, rows: 1 }
  for (let rows = 1; rows <= cards; rows++) {
    if (cards % rows !== 0) continue
    const columns = cards / rows
    if (Math.abs(columns - rows) < Math.abs(best.columns - best.rows)) best = { columns, rows }
  }
  return best
}

/** Repair then validate — what the generation pipeline actually calls. */
export function repairAndValidate(input: unknown): ValidationResult {
  const direct = validateSpec(input)
  if (direct.ok) return direct
  const repaired = validateSpec(repairSpec(input))
  // A repair that does not help should report the original problems.
  return repaired.ok ? repaired : direct
}

// ─────────────────────────────────────────────────────────────
// Result → evidence
// ─────────────────────────────────────────────────────────────

/** Above this share correct, she showed the skill. */
export const DEMONSTRATED_RATIO = 0.8
/** Above this, she has part of it. Below, the work was harder than it should be. */
export const PARTIAL_RATIO = 0.5
/** Strong enough to move the rubric. */
export const LEVEL_EVIDENCE_RATIO = 0.85
/** Under this, leaving is about the moment, not the skill. */
export const MIN_MEANINGFUL_MS = 15_000

/**
 * Playing IS an observation.
 *
 * The reading is deliberately cautious. A strong run is real evidence and can
 * suggest a level. A weak run is a struggle signal that never suggests a level,
 * so one hard afternoon cannot drag the rubric down. Wandering off after a few
 * seconds says something about interest and nothing about ability, so it says
 * nothing at all.
 */
export function resultToEvidence(
  spec: Pick<InteractiveSpec, 'kind' | 'difficulty' | 'targetSkillIds'>,
  result: InteractiveResult,
): SkillEvidence[] {
  if (spec.targetSkillIds.length === 0) return []
  if (result.specKind !== spec.kind) return []

  const signal = signalFor(result)
  if (signal === null) return []

  const ratio = scoreRatio(result)
  const suggestedLevel = suggestLevel(spec.difficulty, ratio, result.completed)
  const quote = evidenceNote(spec.kind, result, ratio)

  return spec.targetSkillIds.map((skillId) => ({
    skillId,
    signal,
    ...(suggestedLevel === undefined ? {} : { suggestedLevel }),
    ...(quote === undefined ? {} : { quote }),
  }))
}

function scoreRatio(result: InteractiveResult): number | undefined {
  if (result.score && result.score.total > 0) {
    return result.score.correct / result.score.total
  }
  if (result.items && result.items.length > 0) {
    return result.items.filter((item) => item.correct).length / result.items.length
  }
  return undefined
}

function signalFor(result: InteractiveResult): EvidenceSignal | null {
  if (!result.completed) {
    // Left almost immediately: that is a read on the moment, not on her.
    if (result.durationMs < MIN_MEANINGFUL_MS) return null
    const ratio = scoreRatio(result)
    if (ratio !== undefined && ratio < PARTIAL_RATIO) return 'struggled'
    return 'partial'
  }

  const ratio = scoreRatio(result)
  // Finished with nothing to score — a story built, a model explored.
  if (ratio === undefined) return 'demonstrated'
  if (ratio >= DEMONSTRATED_RATIO) return 'demonstrated'
  if (ratio >= PARTIAL_RATIO) return 'partial'
  return 'struggled'
}

/**
 * Only a strong finish at a known difficulty implies a level. Failure never
 * suggests one — the level it would imply is a guess, and the rubric is the one
 * thing that has to stay honest.
 */
function suggestLevel(difficulty: Level, ratio: number | undefined, completed: boolean): Level | undefined {
  if (!completed || ratio === undefined) return undefined
  return ratio >= LEVEL_EVIDENCE_RATIO ? difficulty : undefined
}

/** A short factual note for the evidence trail. Growth framing, no comparisons. */
function evidenceNote(
  kind: InteractiveKind,
  result: InteractiveResult,
  ratio: number | undefined,
): string | undefined {
  const label = KIND_LABELS[kind]
  const seconds = Math.round(result.durationMs / 1000)
  if (result.score && result.score.total > 0) {
    return `${label}: ${result.score.correct} of ${result.score.total} in ${seconds}s`
  }
  if (ratio !== undefined) return `${label}: ${Math.round(ratio * 100)}% in ${seconds}s`
  return result.completed ? `${label}: finished in ${seconds}s` : undefined
}
